import React, { useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { getAccountDetails, updateAccountDetails, getCompanyOwnership, saveCompanyOwnership, setOwnerContactVerified } from "../../store/actions/userOnboarding";
import { getAllCountries, getStates, getSignupReasons } from "../../store/actions/shipment.action";
import { validateEmail, validateZipCode } from "../../config/utils";
import CompanyOwnershipSection, {
  buildOwnershipState,
  buildOwnershipPayload,
  validateOwnership,
  OWNERSHIP_FIELD_ORDER,
  RELATIONSHIP_OWNER,
  DEFAULT_OWNER_DESIGNATION,
} from "./CompanyOwnershipSection";
import { CircularProgress } from "@material-ui/core";
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { Paper } from "@material-ui/core";
import { toast } from "react-toastify";
import PropTypes from "prop-types";

const Item = ({ label, value }) => (
  <div className="mb-3">
    <strong>{label}:</strong> <span className="text-muted">{value}</span>
  </div>
);

Item.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string
};

const US_COUNTRY_ID = 218;
const US_COUNTRY_NAME = "United States";
const DUPLICATE_FALLBACK_MSG = 'UserName or Email Already Exists';
const NOT_EDITABLE_FALLBACK_MSG = 'Onboarding user not found or is no longer editable';
const UPDATE_SUCCESS_FALLBACK_MSG = 'Account details updated successfully';
const UPDATE_FAILURE_FALLBACK_MSG = 'Failed to update account details';
const OWNERSHIP_SAVE_FAILURE_MSG = 'Failed to save company ownership details';
const OWNER_VERIFY_FAILURE_MSG = 'Failed to update owner contact verification';

const buildFormState = (src = {}) => ({
  firstName: src.firstName || "",
  lastName: src.lastName || "",
  userName: src.userName || "",
  companyName: src.companyName || "",
  email: src.email || "",
  signUpReason: src.signUpReason || "",
  country: src.country || "",
  state: src.state || "",
  city: src.city || "",
  zipCode: src.zipCode || "",
  phoneNumber: src.phoneNumber || "",
  secondaryPhone: src.secondaryPhone || "",
  addressLine1: src.addressLine1 || "",
  addressLine2: src.addressLine2 || "",
});

export const buildUpdatePayload = (formData, data) => ({
  onboardingId: data?.Id || data?.onboardingId || 0,
  firstName: (formData.firstName || '').trim(),
  lastName: (formData.lastName || '').trim(),
  userName: (formData.userName || '').trim(),
  companyName: formData.companyName || '',
  email: (formData.email || '').trim(),
  signUpReason: formData.signUpReason || '',
  country: formData.country || '',
  state: formData.state || '',
  city: formData.city || '',
  zipCode: formData.zipCode || '',
  phoneNumber: formData.phoneNumber || '',
  secondaryPhone: formData.secondaryPhone || '',
  addressLine1: formData.addressLine1 || '',
  addressLine2: formData.addressLine2 || '',
});

export const extractResponseStatus = (response) => response?.data?.Status;
export const extractResponseMessage = (response) => response?.data?.Message?.AppStatusDescription;
export const extractErrorStatus = (error) => error?.response?.data?.Status || error?.response?.status;
export const extractErrorMessage = (error) => error?.response?.data?.Message?.AppStatusDescription;

export default function AccountDetails(props) {
  const {
    isViewUserRequest,
    data,
    activeStep,
    handleBack,
    handleNext,
    isApprovedSeller,
    isReadOnly = false,
    startInEditMode = false,
    canEditAccountDetails = false,
    onAccountSaved,
  } = props;
  const dispatch = useDispatch();
  const { accountDetails, accountDetailsStatus } = useSelector((state) => state.userOnboarding);
  const countries = useSelector((state) => state?.shipment?.countries);
  const USSates = useSelector((state) => state?.shipment?.states);
  const [userData, setUserData] = useState({});
  const [loader, setLoader] = useState(false);
  const [isEditing, setIsEditing] = useState(Boolean(startInEditMode && canEditAccountDetails));
  const [formData, setFormData] = useState(buildFormState());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [signUpReasonList, setSignUpReasonList] = useState([]);
  const [ownership, setOwnership] = useState(buildOwnershipState());
  const [ownershipErrors, setOwnershipErrors] = useState({});
  const [ownershipReloadKey, setOwnershipReloadKey] = useState(0);
  const [verifying, setVerifying] = useState(false);
  // The onboarding seller fills this section; admins edit it only through the Edit form.
  const canEditOwnership = !isViewUserRequest && !isApprovedSeller && !isReadOnly;
  const isOwnershipEditable = canEditOwnership || isEditing;
  // The View User Request screen is limited to staff, so the onboarding user never gets this action.
  const canMarkOwnerVerified = Boolean(isViewUserRequest) && !isApprovedSeller;

  useEffect(() => {
    if (accountDetailsStatus === "success" || accountDetailsStatus === "failed") {
      setLoader(false);
    } else if (accountDetailsStatus === "pending") {
      setLoader(true);
    }
  }, [accountDetailsStatus]);

  useEffect(() => {
    setUserData(accountDetails || {});
    setFormData(buildFormState(accountDetails));
    setErrors({});
  }, [accountDetails]);

  useEffect(() => {
    dispatch(getAccountDetails(isViewUserRequest, data, isApprovedSeller));
  }, [dispatch, isViewUserRequest, isApprovedSeller, data?.Id, data?.onboardingId]);

  useEffect(() => {
    let active = true;
    getCompanyOwnership(isViewUserRequest ? data?.Id : null, isApprovedSeller, data?.userEmailId || '')
      .then((res) => {
        if (active && res?.data?.Status === 200 && res.data.Entity) {
          const loaded = buildOwnershipState(res.data.Entity);
          setOwnership((prev) => ({ ...loaded, companyName: loaded.companyName || prev.companyName }));
        }
      })
      .catch((e) => {
        console.error('Failed to load company ownership', e);
      });
    return () => {
      active = false;
    };
  }, [isViewUserRequest, isApprovedSeller, data?.Id, data?.userEmailId, ownershipReloadKey]);

  // Company name is captured at signup; use it until the seller edits it.
  useEffect(() => {
    if (accountDetails?.companyName) {
      setOwnership((prev) => (prev.companyName ? prev : { ...prev, companyName: accountDetails.companyName }));
    }
  }, [accountDetails]);

  // Load dropdown datasets only when the super-admin edit affordance is available.
  useEffect(() => {
    if (!canEditAccountDetails) return;
    if (!countries?.length) {
      dispatch(getAllCountries());
    }
    if (!USSates?.length) {
      dispatch(getStates(US_COUNTRY_ID));
    }
    getSignupReasons()
      .then((res) => {
        if (res?.data?.Status === 200 && res?.data?.EntityList?.length) {
          setSignUpReasonList(res.data.EntityList);
        }
      })
      .catch((e) => {
        console.error('Failed to load signup reasons', e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEditAccountDetails]);

  const getButtonText = () => {
    if (activeStep === 0) {
      return "Next";
    }
    if (activeStep === 4) {
      return "Save";
    }
    return "Save & Next";
  };

  const handleFieldChange = (name, value) => {
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'country' && value !== US_COUNTRY_NAME) {
        next.state = '';
      }
      return next;
    });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.firstName.trim()) nextErrors.firstName = 'First Name is required';
    if (!formData.lastName.trim()) nextErrors.lastName = 'Last Name is required';
    if (!formData.userName.trim()) nextErrors.userName = 'User Name is required';
    if (!formData.email.trim()) {
      nextErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email.trim())) {
      nextErrors.email = 'Email is invalid';
    }
    if (formData.zipCode && !validateZipCode(formData.zipCode.trim())) {
      nextErrors.zipCode = 'Zip Code is invalid';
    }
    if (formData.phoneNumber) {
      const digits = formData.phoneNumber.replaceAll(/\D/g, '');
      if (digits.length < 10) {
        nextErrors.phoneNumber = 'Phone Number is invalid';
      }
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleCancel = () => {
    setFormData(buildFormState(accountDetails));
    setErrors({});
    setOwnershipErrors({});
    setOwnershipReloadKey((k) => k + 1);
    setIsEditing(false);
  };

  const handleEnterEdit = () => {
    setFormData(buildFormState(accountDetails));
    setErrors({});
    setIsEditing(true);
  };

  const handleSuccess = (message) => {
    toast.success(message || UPDATE_SUCCESS_FALLBACK_MSG);
    dispatch(getAccountDetails(isViewUserRequest, data, isApprovedSeller));
    setOwnershipReloadKey((k) => k + 1);
    setIsEditing(false);
    if (typeof onAccountSaved === 'function') onAccountSaved();
  };

  const handleDuplicate = (message) => {
    const duplicateMsg = message || DUPLICATE_FALLBACK_MSG;
    setErrors((prev) => ({ ...prev, email: duplicateMsg, userName: duplicateMsg }));
    toast.error(duplicateMsg);
  };

  const handleNotEditable = (message) => {
    toast.error(message || NOT_EDITABLE_FALLBACK_MSG);
    setIsEditing(false);
    if (typeof onAccountSaved === 'function') onAccountSaved();
  };

  const dispatchByStatus = (status, message) => {
    if (status === 200) {
      handleSuccess(message);
      return;
    }
    if (status === 409) {
      handleDuplicate(message);
      return;
    }
    if (status === 404) {
      handleNotEditable(message);
      return;
    }
    toast.error(message || UPDATE_FAILURE_FALLBACK_MSG);
  };

  const handleSave = async () => {
    const isAccountValid = validate();
    const ownershipErrs = validateOwnership(ownership, formData.phoneNumber, formData.email);
    setOwnershipErrors(ownershipErrs);
    if (!isAccountValid || focusFirstInvalidOwnershipField(ownershipErrs)) return;
    try {
      setSaving(true);
      const ownershipResponse = await saveCompanyOwnership(buildOwnershipPayload(ownership, data));
      if (extractResponseStatus(ownershipResponse) !== 200) {
        toast.error(extractResponseMessage(ownershipResponse) || OWNERSHIP_SAVE_FAILURE_MSG);
        return;
      }
      // Company name is edited in the ownership section, so it wins over the account form value.
      const response = await updateAccountDetails({ ...buildUpdatePayload(formData, data), companyName: ownership.companyName.trim() });
      dispatchByStatus(extractResponseStatus(response), extractResponseMessage(response));
    } catch (error) {
      const status = extractErrorStatus(error);
      const message = extractErrorMessage(error);
      if (status === 409) handleDuplicate(message);
      else toast.error(message || UPDATE_FAILURE_FALLBACK_MSG);
    } finally {
      setSaving(false);
    }
  };

  const handleOwnershipChange = (name, value) => {
    setOwnership((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'relationshipType' && value === RELATIONSHIP_OWNER && !prev.designation.trim()) {
        next.designation = DEFAULT_OWNER_DESIGNATION;
      }
      if (name === 'whatsAppSameAsPhone' && value) {
        next.ownerWhatsAppNumber = prev.ownerDirectPhone;
      }
      if (name === 'ownerDirectPhone' && prev.whatsAppSameAsPhone) {
        next.ownerWhatsAppNumber = value;
      }
      return next;
    });
    setOwnershipErrors((prev) => ({ ...prev, [name]: '', ...(name === 'ownerDirectPhone' && { ownerWhatsAppNumber: '' }) }));
  };

  const focusFirstInvalidOwnershipField = (errs) => {
    const firstKey = OWNERSHIP_FIELD_ORDER.find((key) => errs[key]);
    if (!firstKey) return false;
    const targetId = firstKey === 'relationshipType' ? `co-relationship-${RELATIONSHIP_OWNER}` : `co-${firstKey}`;
    document.getElementById(targetId)?.focus();
    return true;
  };

  const handleNextClick = async () => {
    if (!canEditOwnership) {
      handleNext();
      return;
    }
    const errs = validateOwnership(ownership, userData.phoneNumber, userData.email);
    setOwnershipErrors(errs);
    if (focusFirstInvalidOwnershipField(errs)) return;
    try {
      setSaving(true);
      const response = await saveCompanyOwnership(buildOwnershipPayload(ownership, data));
      if (extractResponseStatus(response) === 200) {
        handleNext();
      } else {
        toast.error(extractResponseMessage(response) || OWNERSHIP_SAVE_FAILURE_MSG);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || OWNERSHIP_SAVE_FAILURE_MSG);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleOwnerVerified = async () => {
    const isVerified = !ownership.isOwnerContactVerified;
    try {
      setVerifying(true);
      const response = await setOwnerContactVerified({ onboardingId: data?.Id || data?.onboardingId || 0, isVerified });
      if (extractResponseStatus(response) === 200) {
        setOwnership((prev) => ({ ...prev, isOwnerContactVerified: isVerified }));
        toast.success(extractResponseMessage(response));
        if (typeof onAccountSaved === 'function') onAccountSaved();
      } else {
        toast.error(extractResponseMessage(response) || OWNER_VERIFY_FAILURE_MSG);
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || OWNER_VERIFY_FAILURE_MSG);
    } finally {
      setVerifying(false);
    }
  };

  const showEditButton = canEditAccountDetails && !isEditing && accountDetailsStatus === 'success';
  const isUsCountry = (formData.country || '').trim() === US_COUNTRY_NAME;

  const renderTextField = (name, label, { required = false, type = 'text' } = {}) => (
    <div className="mb-3">
      <div>
        {label}
        {required && <span className="error"> *</span>}
      </div>
      <input
        type={type}
        name={name}
        value={formData[name] || ''}
        onChange={(e) => handleFieldChange(name, e.target.value)}
        className="form-control"
      />
      {errors[name] && <p className="error dialogError">{errors[name]}</p>}
    </div>
  );

  const renderOwnershipSection = () => (
    <CompanyOwnershipSection
      form={ownership}
      errors={ownershipErrors}
      editable={isOwnershipEditable}
      onChange={handleOwnershipChange}
      accountDetails={userData}
      canMarkVerified={canMarkOwnerVerified}
      verifying={verifying}
      onToggleVerified={handleToggleOwnerVerified}
    />
  );

  const renderReadOnlyPanel = () => (
    <>
      <Row className="my-4">
        <Col md={6} lg={6}>
          <Item label="First Name" value={userData.firstName} />
          <Item label="User Name" value={userData.userName} />
          <Item label="Email" value={userData.email} />
        </Col>
        <Col md={6} lg={6}>
          <Item label="Last Name" value={userData.lastName} />
          <Item label="Company" value={userData.companyName} />
          <Item label="Sign-Up Reason" value={userData.signUpReason} />
        </Col>
      </Row>

      {renderOwnershipSection()}

      <h6 className="fw-bold mb-3 font-20">Address Details</h6>
      <hr className="my-4" />

      <Row>
        <Col md={6} lg={6}>
          <Item label="Country" value={userData.country} />
          <Item label="State" value={userData.state} />
          <Item label="Phone Number" value={userData.phoneNumber} />
          <Item label="Address Line 1" value={userData.addressLine1} />
        </Col>
        <Col md={6} lg={6}>
          <Item label="Zip Code" value={userData.zipCode} />
          <Item label="City" value={userData.city} />
          <Item label="Secondary Phone" value={userData.secondaryPhone} />
          <Item label="Address Line 2" value={userData.addressLine2} />
        </Col>
      </Row>
    </>
  );

  const renderEditPanel = () => (
    <>
      <Row className="my-4">
        <Col md={6} lg={6}>
          {renderTextField('firstName', 'First Name', { required: true })}
          {renderTextField('userName', 'User Name', { required: true })}
          {renderTextField('email', 'Email', { required: true })}
        </Col>
        <Col md={6} lg={6}>
          {renderTextField('lastName', 'Last Name', { required: true })}
          <div className="mb-3">
            <div>Sign-Up Reason</div>
            <select
              name="signUpReason"
              value={formData.signUpReason || ''}
              onChange={(e) => handleFieldChange('signUpReason', e.target.value)}
              className="form-control"
            >
              <option value="">Select</option>
              {formData.signUpReason && !signUpReasonList.some((r) => r.signupReason === formData.signUpReason) && (
                <option value={formData.signUpReason}>{formData.signUpReason}</option>
              )}
              {signUpReasonList.map((reason) => (
                <option key={reason.id} value={reason.signupReason}>{reason.signupReason}</option>
              ))}
            </select>
          </div>
        </Col>
      </Row>

      {renderOwnershipSection()}

      <h6 className="fw-bold mb-3 font-20">Address Details</h6>
      <hr className="my-4" />

      <Row>
        <Col md={6} lg={6}>
          <div className="mb-3">
            <div>Country</div>
            <select
              name="country"
              value={formData.country || ''}
              onChange={(e) => handleFieldChange('country', e.target.value)}
              className="form-control"
            >
              <option value="">Select</option>
              {formData.country && !(countries || []).some((c) => c.name === formData.country) && (
                <option value={formData.country}>{formData.country}</option>
              )}
              {(countries || []).map((country) => (
                <option key={country.id} value={country.name}>{country.name}</option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <div>State</div>
            {isUsCountry ? (
              <select
                name="state"
                value={formData.state || ''}
                onChange={(e) => handleFieldChange('state', e.target.value)}
                className="form-control"
              >
                <option value="">Select</option>
                {(USSates || []).map((s) => (
                  <option key={s.Id} value={s.StateName || ''} disabled={!s.StateName}>
                    {s.StateName}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                name="state"
                value={formData.state || ''}
                onChange={(e) => handleFieldChange('state', e.target.value)}
                className="form-control"
              />
            )}
          </div>
          {renderTextField('phoneNumber', 'Phone Number')}
          {renderTextField('addressLine1', 'Address Line 1')}
        </Col>
        <Col md={6} lg={6}>
          {renderTextField('zipCode', 'Zip Code')}
          {renderTextField('city', 'City')}
          {renderTextField('secondaryPhone', 'Secondary Phone')}
          {renderTextField('addressLine2', 'Address Line 2')}
        </Col>
      </Row>
    </>
  );

  return (
    <Container fluid>
      <Paper className='onboardingFormWrapping fw-3' elevation={2} square>
        {(loader || saving) && (<div className="full-screen-spinner-overlay"><CircularProgress disableShrink /></div>)}
        <div className="flexDataTable">
          <h6 className="fw-bold mb-3 font-20">Account Details</h6>
          {showEditButton && (
            <Button
              disableElevation
              variant="contained"
              color="primary"
              onClick={handleEnterEdit}
              className='cus-seconday-bg-btn btn'
            >
              Edit
            </Button>
          )}
        </div>
        <hr className="my-4" />
        {isEditing ? renderEditPanel() : renderReadOnlyPanel()}
      </Paper>
      <div className='flexPayment mt-2'>
        {!isViewUserRequest ? <Typography component="em">* Please complete the form to finalize your onboarding process</Typography> : <Typography component="em"></Typography>}
        <div className="mt-3 alignItemsCenter">
          {isEditing ? (
            <>
              <Button onClick={handleCancel} disableElevation disabled={saving}>
                Cancel
              </Button>
              <Button
                disableElevation
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={saving}
                className='cus-seconday-bg-btn btn ml-2'
              >
                Save
              </Button>
            </>
          ) : (
            <>
              <Button disabled={activeStep === 0} onClick={handleBack} disableElevation>
                Back
              </Button>
              <Button
                disableElevation
                variant="contained"
                color="primary"
                onClick={handleNextClick}
                className='cus-seconday-bg-btn btn ml-2'
              >
                {getButtonText()}
              </Button>
            </>
          )}
        </div>
      </div>
    </Container>
  );
};


AccountDetails.propTypes = {
  isViewUserRequest: PropTypes.bool,
  data: PropTypes.object,
  isApprovedSeller: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  activeStep: PropTypes.number,
  handleBack: PropTypes.func,
  handleNext: PropTypes.func,
  startInEditMode: PropTypes.bool,
  canEditAccountDetails: PropTypes.bool,
  onAccountSaved: PropTypes.func,
}
